module Client.Components.EquipmentLookup

open Client
open Client.Services
open Shared
open global.Elmish
open Fable.Core.JsInterop
open System
open Thoth.Fetch
open Thoth.Json
open Fetch.Types
open Feliz
open Feliz.MaterialUI
open Fable.MaterialUI.Icons
open Fable.MaterialUI.MaterialDesignIcons
open Feliz.SweetAlert
open Feliz.UseElmish
open Fable.Core.Experimental
open Fable.Core.JS

let strOption (s : string) = if String.IsNullOrWhiteSpace s then None else Some (s.Trim())

type EquipmentLookupFailureType =
  | DomainFailure of string
  | GeneralError of Exception
  | RetryLimitReached

type LookupRequestState =
  { EquipmentOrSerialNumber : string option
    ZipCode : string option }

type EquipmentLookupFailureResult =
  { LookupRequest : EquipmentLookupRequest
    FailureType : EquipmentLookupFailureType } with
      member this.toString() =
        match this.FailureType with
        | DomainFailure v -> v
        | RetryLimitReached -> "Unable to locate equipment. Retry limit reached";
        | GeneralError exn -> sprintf "Api error: %s" exn.Message

type LookupResultState =
  | Searching
  | Initialized
  | LookupFailed of EquipmentLookupFailureResult

type Model = { LookupRequest : LookupRequestState; Loading : LookupResultState; ResultCallBack : (Result<Shared.Equipment,EquipmentLookupFailureResult> -> unit) }

type Msg =
  | UpdateLookupRequest of LookupRequestState
  | LookupRequest of Shared.EquipmentLookupRequest
  | LookupResult of Result<Shared.Equipment,EquipmentLookupFailureResult>
  | AbandonLookup of EquipmentLookupFailureResult

let getEntries (request : Shared.EquipmentLookupRequest) : Promise<Result<Result<Shared.Equipment,string>,FetchError>> =
  promise {
    let decoder = Services.decoder<Result<Shared.Equipment,string>>
    return! Fetch.tryPost<_,Result<Shared.Equipment,string>>("/api/Equipments/Lookup", request, decoder = decoder)
  }

let getEntriesCmd request =
  Cmd.OfPromise.either getEntries request
    (fun fetchResult ->
      match fetchResult with
      | Ok x -> x |> Result.mapError (fun y -> { FailureType = DomainFailure y; LookupRequest = request;}) |> LookupResult
      | Error (FetchFailed r) when r.Status = 429 ->
        Error { FailureType = RetryLimitReached; LookupRequest = request;} |> LookupResult
      | Error _ ->
        Error { FailureType = DomainFailure "General failure. Please retry."; LookupRequest = request;} |> LookupResult
    )
    (fun x ->
      { FailureType = GeneralError x; LookupRequest = request;} |> Error |> LookupResult)

let init (equipmentOrSerialNumberOption, resultCallBack) =
  { LookupRequest = { EquipmentOrSerialNumber = equipmentOrSerialNumberOption; ZipCode = None; }; Loading = Initialized; ResultCallBack = resultCallBack; }, Cmd.none

let update msg (model : Model) =
  match msg with
  | UpdateLookupRequest r ->
    { model with LookupRequest = r }, Cmd.none
  | LookupRequest r ->
    { model with Loading = Searching; }, getEntriesCmd r
  | LookupResult r ->
    match r with
    | Ok v ->
      model.ResultCallBack r
      { model with Loading = Initialized; }, Cmd.none
    | Error msg when msg.FailureType = RetryLimitReached ->
      model, Cmd.ofMsg (AbandonLookup msg)
    | Error msg ->
      { model with Loading = LookupFailed msg; }, Cmd.none
  | AbandonLookup failureResult ->
    model.ResultCallBack (Error failureResult)
    model, Cmd.none

let view = React.functionComponent (fun (inputProps: {| reCaptchaSiteKey : string; equipmentOrSerialNumberOption : string Option; returnResult: Result<Shared.Equipment,EquipmentLookupFailureResult> -> unit; |}) ->
  let model, dispatch = React.useElmish(init (inputProps.equipmentOrSerialNumberOption,inputProps.returnResult), update, [| |])
  let classes = Styles.useStyles()
  let buttonProgressIndicator =
    Mui.circularProgress [
      prop.className classes.buttonProgress
      prop.size 24
    ]
  let effect () = ReCaptcha.initialize inputProps.reCaptchaSiteKey
  Feliz.React.useEffect (effect , [||])
  Mui.grid [
    grid.container true
    grid.direction.column
    grid.spacing._2
    grid.children [
      Mui.grid [
        grid.item true
        grid.children [
          Html.form [
            prop.children [
              Mui.textField [
                match model.LookupRequest.EquipmentOrSerialNumber with
                | None -> ()
                | Some e -> textField.defaultValue e
                textField.placeholder "Equipment ID, Asset, or Serial Number"
                textField.variant.outlined
                textField.margin.normal
                textField.fullWidth true
                textField.label "Equipment ID, Asset, or Serial Number"
                textField.name "EquipmentOrSerialNumber"
                textField.required true
                textField.onChange (fun (ev : Browser.Types.Event) -> { model.LookupRequest with EquipmentOrSerialNumber = strOption !!ev.target?value } |> UpdateLookupRequest |> dispatch)
              ]
              Mui.textField [
                textField.placeholder "Zip Code (Equipment Location)"
                textField.variant.outlined
                textField.margin.normal
                textField.fullWidth true
                textField.label "Zip code (Equipment Location)"
                textField.name "ZipCode"
                textField.required true
                textField.onChange (fun (ev : Browser.Types.Event) -> { model.LookupRequest with ZipCode = strOption !!ev.target?value } |> UpdateLookupRequest |> dispatch)
              ]
              match model.Loading with
              | Initialized
              | Searching ->
                Html.div [
                  prop.className classes.wrapper
                  prop.children [
                    Mui.button [
                      prop.type'.submit
                      button.fullWidth true
                      button.variant.contained
                      button.color.primary
                      button.children "Search"
                      button.disabled (model.Loading = LookupResultState.Searching || model.LookupRequest.EquipmentOrSerialNumber.IsNone || model.LookupRequest.ZipCode.IsNone)
                      prop.onClick (fun e ->
                        e.preventDefault ()
                        match model.LookupRequest.EquipmentOrSerialNumber, model.LookupRequest.ZipCode with
                        | Some e, Some z ->
                          let fn token = { Shared.EquipmentLookupRequest.EquipmentOrSerialNumber = e; ZipCode = z; RecaptchaResponse = token; } |> LookupRequest |> dispatch
                          ReCaptcha.registerCallBack inputProps.reCaptchaSiteKey Constants.recaptchaLookupEquipmentAction fn
                        | _ -> ()
                      )
                    ]
                    if LookupResultState.Searching = model.Loading then buttonProgressIndicator else Html.none
                  ]
                ]
              | LookupFailed failureResult ->
                let msg =
                  match failureResult.FailureType with
                  | EquipmentLookupFailureType.DomainFailure v -> v
                  | RetryLimitReached -> "Unable to locate equipment. Retry limit reached";
                  | EquipmentLookupFailureType.GeneralError _ -> "General failure. Please retry."
                Html.div [
                  prop.className classes.wrapper
                  prop.children [
                    Mui.alert [
                      alert.severity.warning
                      alert.children msg
                    ]
                  ]
                ]
                Mui.buttonGroup [
                  buttonGroup.fullWidth true
                  prop.className classes.wrapper
                  prop.children [
                    Mui.button [
                      prop.type'.submit
                      button.fullWidth true
                      button.variant.contained
                      button.color.primary
                      button.children "Retry"
                      button.disabled (model.LookupRequest.EquipmentOrSerialNumber.IsNone || model.LookupRequest.ZipCode.IsNone)
                      prop.onClick (fun e ->
                        e.preventDefault ()
                        match model.LookupRequest.EquipmentOrSerialNumber, model.LookupRequest.ZipCode with
                        | Some e, Some z ->
                          let fn token = { Shared.EquipmentLookupRequest.EquipmentOrSerialNumber = e; ZipCode = z; RecaptchaResponse = token; } |> LookupRequest |> dispatch
                          ReCaptcha.registerCallBack inputProps.reCaptchaSiteKey Constants.recaptchaLookupEquipmentAction fn
                        | _ -> ()
                      )
                    ]
                    Mui.button [
                      prop.type'.button
                      button.fullWidth true
                      button.variant.contained
                      button.color.default'
                      button.children "Contact DEX"
                      prop.onClick (fun e ->
                        e.preventDefault ()
                        AbandonLookup failureResult |> dispatch
                      )
                    ]
                  ]
                ]
            ]
          ]
        ]
      ]
    ]
  ]
)